import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { GridComponent, ColumnsDirective, ColumnDirective, Inject, Sort, Filter, Search, Toolbar, VirtualScroll, Reorder } from '@syncfusion/ej2-react-grids';
import { Link } from 'react-router-dom';

import useFetchSites from '../hooks/useFetchSites';

import Loader from '../components/Loader';

import SearchBar from '../utils/SearchBar';
import CustomSelect from '../utils/CustomSelect';

import { formatCurrency } from '../utils/Format';

const statusTemplate = (props) => {
    const normalizedStatus = props.Etat.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

    const statusColors = {
        'ouvert': 'bg-green-500',
        'cloture': 'bg-red-500',
        'a suivre': 'bg-yellow-500',
        'pre-rapport': 'bg-blue-500',
        'devis valide': 'bg-purple-500',
        'sans suite': 'bg-red-700',
    };

    let statusColor = 'bg-gray-400';
    for (const [key, value] of Object.entries(statusColors)) {
        if (normalizedStatus.startsWith(key)) {
            statusColor = value;
            break;
        }
    }

    return (
        <span className={`px-2 py-1 rounded text-white ${statusColor}`}>
            {props.Etat}
        </span>
    );
};

// function emptyMessageTemplate(props, loading) {
//     if (loading) {
//         const numberOfMessages = 15;
//         return (
//             <div>
//                 {Array.from({ length: numberOfMessages }).map((_, index) => (
//                     <div key={index} className="flex flex-row flex-start gap-4 p-3 border-b-2 text-gray-500">
//                         {Array.from({ length: 5 }).map((_, subIndex) => (
//                             <div
//                                 key={subIndex}
//                                 className="bg-gray-200 rounded-md h-4 animate-pulse"
//                                 style={{
//                                     width: `${Math.floor(Math.random() * (40 - 10 + 1)) + 10}rem`,
//                                 }}
//                             ></div>
//                         ))}
//                     </div>
//                 ))}
//             </div>
//         );
//     }

//     return <div className="p-4 text-gray-500 text-start">Aucun résultat trouvé.</div>;
// }

const ListSite = ({ lstSites, showAddButton = true, size = 520, setSelectedRow = null, handleRowDoubleClick = null, loading = false }) => {
    const emptyMessageTemplate = useCallback(() => {
        if (loading) {
            return (
                <div>
                    {Array.from({ length: 15 }).map((_, i) => (
                        <div key={i} className="flex flex-row gap-4 p-3 border-b-2 text-gray-500">
                            {Array.from({ length: 5 }).map((_, j) => (
                                <div key={j} className="bg-gray-200 rounded-md h-4 animate-pulse" style={{ width: `${Math.random() * 30 + 10}rem` }}></div>
                            ))}
                        </div>
                    ))}
                </div>
            );
        }
        return <div className="p-4 text-gray-500 text-start">Aucun résultat trouvé.</div>;
    }, [loading]);


    const gridRef = useRef();

    const [filtersVisible, setFiltersVisible] = useState(false);

    const [query, setQuery] = useState("");

    const norm = (s) =>
        (s ?? "")
            .toString()
            .toLowerCase()
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "");

    const SEARCH_FIELDS = ['Nom', 'Code_Chantier', 'Adresse1', 'Code_compta', 'CP', 'Ville'];

    const [selectedSociete, setSelectedSociete] = useState("Toutes");
    const [selectedEntite, setSelectedEntite] = useState("Toutes");
    const [selectedEtat, setSelectedEtat] = useState("Toutes");
    const [selectedType, setSelectedType] = useState("Toutes");

    const [filteredData, setFilteredData] = useState([]);

    useEffect(() => {
        let filtered = lstSites;

        filtered = filtered.filter(site => site.Nom && site.Nom.trim() !== '');

        if (query.trim()) {
            const q = norm(query);
            filtered = filtered.filter((d) =>
                SEARCH_FIELDS.some((f) => norm(d?.[f]).includes(q))
            );
        }

        if (selectedSociete !== "Toutes") {
            filtered = filtered.filter(site => site.Societe === selectedSociete);
        }

        if (selectedEntite !== "Toutes") {
            filtered = filtered.filter(site => site.Entite === selectedEntite);
        }

        if (selectedEtat !== "Toutes") {
            filtered = filtered.filter(site => site.Etat === selectedEtat);
        }

        if (selectedType !== "Toutes") {
            filtered = filtered.filter(site => site.Domaine_Activite === selectedType);
        }

        setFilteredData(filtered);
    }, [query, selectedSociete, selectedEntite, selectedEtat, selectedType, lstSites]);

    // if (loading) {
    //     return <div className="flex flex-1 justify-center items-center"><Loader /></div>;
    // }

    // if (error) {
    //     return <div>Erreur : {error}</div>;
    // }

    const optSociete = ["Toutes", ...new Set(filteredData.map(site => site.Societe).filter(societe => societe))];
    const optEntite = ["Toutes", ...new Set(filteredData.map(site => site.Entite).filter(entite => entite))];
    const optEtat = ["Toutes", ...new Set(filteredData.map(site => site.Etat).filter(etat => etat))];
    const optType = ["Toutes", ...new Set(filteredData.map(site => site.Domaine_Activite).filter(type => type))];

    const formattedData = useMemo(() => {
        return filteredData.map(site => ({
            ...site,
            Total_Pathologie: formatCurrency(site.Total_Pathologie),
            Total_A_Payer: formatCurrency(site.Total_A_Payer),
        }));
    }, [filteredData]);

    useEffect(() => {
        if (gridRef.current) {
            gridRef.current.dataSource = formattedData;
            gridRef.current.dataBind();
        }
    }, [formattedData]);

    const totalPathologie = filteredData.reduce(
        (sum, site) => sum + (site.Total_Pathologie || 0),
        0
    );

    const totalAPayer = filteredData.reduce(
        (sum, site) => sum + (site.Total_A_Payer || 0),
        0
    );

    const siteCount = Array.isArray(filteredData) ? filteredData.length : 0;

    return (
        <div className="bg-white p-4 rounded-lg">

            <div className="lg:mx-auto mb-4 lg:mb-8 px-6 container">
                <div className="flex justify-between mb-4">
                    <button
                        onClick={() => setFiltersVisible(!filtersVisible)}
                        className="flex items-center text-gray-500 hover:text-orange-600 text-sm"
                    >
                        {filtersVisible ? (
                            <i className="fa-chevron-up fas"></i>
                        ) : (
                            <i className="fa-chevron-down fas"></i>
                        )}
                        <span className="ml-2">{filtersVisible ? 'Réduire' : 'Afficher'} les filtres</span>
                    </button>
                    <div className="w-[20rem]">
                        <SearchBar
                            value={query}
                            onChange={setQuery}
                            onClear={() => setQuery("")}
                            placeholder="Rechercher …"
                            disabled={loading}
                        />
                    </div>
                </div>
                {filtersVisible && (
                    <div className="flex flex-start items-center gap-4 pb-6">
                        <div className="md:flex justify-between gap-4 w-1/2">
                            <CustomSelect
                                label={"Société"}
                                options={optSociete}
                                value={selectedSociete}
                                onChange={setSelectedSociete}
                            />
                            <CustomSelect
                                label={"Entité/Région"}
                                options={optEntite}
                                value={selectedEntite}
                                onChange={setSelectedEntite}
                            />
                        </div>
                        <div className="md:flex gap-4 w-1/2">
                            <CustomSelect
                                label={"État"}
                                options={optEtat}
                                value={selectedEtat}
                                onChange={setSelectedEtat}
                            />
                            <CustomSelect
                                label={"Type d'Ouvrage"}
                                options={optType}
                                value={selectedType}
                                onChange={setSelectedType}
                            />

                        </div>
                    </div>
                )}

                <div className="border-2 border-gray-300 hover:border-orange-400 rounded w-full transition duration-300 ease-in-out grow">

                    <GridComponent
                        ref={gridRef}
                        dataSource={formattedData}
                        recordDoubleClick={handleRowDoubleClick ? handleRowDoubleClick : null}
                        emptyRecordTemplate={emptyMessageTemplate}
                        allowSorting={true}
                        allowReordering={true}
                        enableVirtualization={true}
                        enableStickyHeader={true}
                        loadingIndicator={{ indicatorType: 'Shimmer' }}
                        height={520}
                        sortSettings={{
                            columns: [{ field: '_Date_Crea', direction: 'Descending' }]
                        }}>
                        <ColumnsDirective>
                            <ColumnDirective field="Nom" headerText="Nom" width="280" />
                            <ColumnDirective field="Societe" headerText="Société" width="150" />
                            <ColumnDirective field="Entite" headerText="Entité" width="250" />
                            <ColumnDirective field="Etat" template={statusTemplate} headerText="État" width="120" />
                            <ColumnDirective
                                field="Total_Pathologie"
                                headerText="Coût Pathologie"
                                type="number"
                                width="150"
                                format={formatCurrency}
                                textAlign="Right"
                            />
                            <ColumnDirective
                                field="Total_A_Payer"
                                headerText="Coût Déclaration"
                                type="number"
                                width="150"
                                textAlign="Right"
                            />
                            <ColumnDirective
                                field="Date_DOC_String"
                                headerText="DOC"
                                width="150"
                                type="date"
                                format="dd/MM/yyyy"
                                textAlign="Center"
                            />
                            <ColumnDirective
                                field="Date_PVRecep_String"
                                headerText="PV Récep."
                                width="150"
                                type="date"
                                format="dd/MM/yyyy"
                                textAlign="Center"
                            />
                            <ColumnDirective
                                field="Date_Prev_Trans_String"
                                headerText="Transfert Prévu"
                                width="150"
                                type="date"
                                format="dd/MM/yyyy"
                                textAlign="Center"
                            />
                            <ColumnDirective
                                field="Date_Transfert_String"
                                headerText="Transfert"
                                width="150"
                                type="date"
                                format="dd/MM/yyyy"
                                textAlign="Center"
                            />
                            <ColumnDirective field="Code_Chantier" headerText="Code Chantier" width="150" />
                            <ColumnDirective field="Adresse1" headerText="Adresse" width="200" />
                            <ColumnDirective field="Code_compta" headerText="Code Compta" width="150" />
                            <ColumnDirective field="CP" headerText="CP" width="100" />
                            <ColumnDirective field="Ville" headerText="Ville" width="180" />
                            <ColumnDirective field="Domaine_Activite" headerText="Type d'Ouvrage" width="200" />
                            <ColumnDirective field="Me_Ouvrage" headerText="ME Ouvrage" width="200" />
                            <ColumnDirective field="Proprietaire" headerText="Propriétaire" width="150" />
                            <ColumnDirective
                                field="_Date_Modif"
                                headerText="Modifié le"
                                width="150"
                                type="date"
                                format="dd/MM/yyyy"
                                textAlign="Center"
                            />
                            <ColumnDirective
                                field="_Date_Crea"
                                headerText="Créé le"
                                width="150"
                                type="date"
                                format="dd/MM/yyyy"
                                textAlign="Center"
                            />
                        </ColumnsDirective>
                        <Inject services={[Sort, Filter, Search, Toolbar, VirtualScroll, Reorder]} />
                    </GridComponent>
                </div>
            </div>
            <div className="mb-4 grid grid-cols-1 md:grid-cols-3 gap-4 px-6">

                {/* Nombre de sites */}
                <div className="flex items-center justify-between bg-orange-50 border border-orange-200 rounded-xl px-4 py-3">
                    <div>
                        <p className="text-xs font-semibold text-orange-500 uppercase tracking-wide">
                            Nombre de sites
                        </p>
                        <p className="mt-1 text-xl font-bold text-gray-800">
                            {siteCount ? siteCount : "Aucun site disponible"}
                        </p>
                    </div>
                    <div className="flex items-center justify-center w-10 h-10 bg-orange-100 rounded-full">
                        <i className="fa-solid fa-city text-orange-500" />
                    </div>
                </div>

                {/* Total Pathologie */}
                <div className="flex items-center justify-between bg-orange-50 border border-orange-200 rounded-xl px-4 py-3">
                    <div>
                        <p className="text-xs font-semibold text-orange-500 uppercase tracking-wide">
                            Total Coût Pathologie
                        </p>
                        <p className="mt-1 text-xl font-bold text-gray-800">
                            {formatCurrency(totalPathologie)}
                        </p>
                    </div>
                    <div className="flex items-center justify-center w-10 h-10 bg-orange-100 rounded-full">
                        <i className="fa-solid fa-file-invoice-dollar text-orange-500" />
                    </div>
                </div>

                {/* Total Déclaration */}
                <div className="flex items-center justify-between bg-orange-50 border border-orange-200 rounded-xl px-4 py-3">
                    <div>
                        <p className="text-xs font-semibold text-orange-500 uppercase tracking-wide">
                            Total Coût Déclaration
                        </p>
                        <p className="mt-1 text-xl font-bold text-gray-800">
                            {formatCurrency(totalAPayer)}
                        </p>
                    </div>
                    <div className="flex items-center justify-center w-10 h-10 bg-orange-100 rounded-full">
                        <i className="fa-solid fa-file-invoice-dollar text-orange-500" />
                    </div>
                </div>
            </div>

        </div>
    );
};

export default ListSite;